import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
  Card,
} from "react-bootstrap";
import {
  IconBug,
  IconMessageCircle,
  IconCopy,
  IconRefresh,
  IconUser,
  IconRobot,
  IconAlertTriangle,
  IconX,
  IconMaximize,
  IconMinimize,
} from "@tabler/icons-react";
import useDebugAnalysis from "../hooks/useDebugAnalysis";

const ConversationDebugModal = ({
  show,
  onHide,
  messages = [],
  agentContext = null,
}) => {
  // Estados locais
  const [customPrompt, setCustomPrompt] = useState("");
  const [analysisResult, setAnalysisResult] = useState("");
  const [copied, setCopied] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Hook de debug
  const { loading, error, resetError, analyzeConversation, defaultPrompts } =
    useDebugAnalysis();

  // Reset ao fechar
  useEffect(() => {
    if (!show) {
      setAnalysisResult("");
      setCustomPrompt("");
      setCopied(false);
      setIsExpanded(false);
      resetError();
    }
  }, [show, resetError]);

  // Set prompt padrão quando abre
  useEffect(() => {
    if (show && !customPrompt && defaultPrompts.conversation) {
      setCustomPrompt(defaultPrompts.conversation);
    }
  }, [show, customPrompt, defaultPrompts]);

  // Estatísticas da conversa
  const getConversationStats = () => {
    const userMessages = messages.filter((msg) => msg.isUser && !msg.isWelcome);
    const agentMessages = messages.filter(
      (msg) => !msg.isUser && !msg.isWelcome && !msg.isError
    );
    const errorMessages = messages.filter((msg) => msg.isError);
    const totalCharacters = messages.reduce(
      (total, msg) => total + (msg.text?.length || 0),
      0
    );

    return {
      total: messages.filter((msg) => !msg.isWelcome).length,
      userMessages: userMessages.length,
      agentMessages: agentMessages.length,
      errorMessages: errorMessages.length,
      totalCharacters,
    };
  };

  const stats = getConversationStats();

  // Formatar conversa para análise
  const formatConversationForAnalysis = () => {
    const agentName = agentContext?.agent_name || "AGENTE";
    return messages
      .filter((msg) => !msg.isWelcome && !msg.isError && !msg.isWarning)
      .map((msg, index) => {
        const timestamp = msg.timestamp || new Date().toLocaleTimeString();
        const speaker = msg.isUser ? "USUÁRIO" : agentName.toUpperCase();
        const content = msg.isAudio ? "[Mensagem de áudio]" : msg.text;
        return `[${timestamp}] ${speaker}: ${content}`;
      })
      .join("\n\n");
  };

  // Executar análise
  const handleAnalyze = async () => {
    if (messages.length <= 1) {
      setAnalysisResult(
        "⚠️ Não há conversa suficiente para análise. Tente conversar um pouco mais com o agente primeiro!"
      );
      return;
    }

    const conversationHistory = formatConversationForAnalysis();
    const result = await analyzeConversation(conversationHistory, customPrompt);

    if (result.success) {
      setAnalysisResult(result.analysis);
    } else {
      setAnalysisResult(
        `❌ Erro na análise: ${result.error || "Erro inesperado"}`
      );
    }
  };

  // Copiar resultado
  const handleCopy = () => {
    navigator.clipboard.writeText(analysisResult);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Renderizar preview da conversa
  const renderConversationPreview = () => {
    const previewMessages = messages.filter((msg) => !msg.isWelcome).slice(-6); // Últimas 6 mensagens

    return (
      <div
        className="debug-conversation-preview"
        style={{ maxHeight: "200px", overflowY: "auto" }}
      >
        {previewMessages.map((msg, index) => (
          <Card
            key={msg.id || index}
            className={`mb-2 debug-context-card ${
              msg.isUser
                ? "debug-user-message"
                : msg.isError
                ? "debug-error-message"
                : "debug-agent-message"
            }`}
            style={{ fontSize: "12px" }}
          >
            <Card.Body style={{ padding: "8px 12px" }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                {msg.isUser ? <IconUser size={14} /> : <IconRobot size={14} />}
                <small className="fw-bold">
                  {msg.isUser
                    ? "Usuário"
                    : agentContext?.agent_name || "Agente"}
                </small>
                <small className="text-muted">{msg.timestamp}</small>
                {msg.isError && (
                  <IconAlertTriangle size={12} className="text-danger" />
                )}
              </div>
              <div style={{ lineHeight: "1.3" }}>
                {msg.isAudio ? (
                  <em className="text-muted">🎤 Mensagem de áudio</em>
                ) : (
                  msg.text?.substring(0, 150) +
                  (msg.text?.length > 150 ? "..." : "")
                )}
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className="debug-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <IconBug size={20} />
          Análise da Conversa
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Preview da Conversa */}
        <div className="mb-3">
          <h6 className="d-flex align-items-center gap-2 mb-2">
            <IconMessageCircle size={16} />
            Preview da Conversa
          </h6>
          {renderConversationPreview()}
        </div>

      

        {/* Botão de Análise */}
        <div className="d-flex gap-2 mb-3">
          <Button
            variant="primary"
            onClick={handleAnalyze}
            disabled={loading || !customPrompt.trim() || messages.length <= 1}
            className="d-flex align-items-center gap-2"
          >
            {loading ? <Spinner size="sm" /> : <IconBug size={16} />}
            {loading ? "Analisando..." : "Analisar Conversa"}
          </Button>

         
        </div>

        {/* Error Display */}
        {error && (
          <Alert variant="danger" className="d-flex align-items-center gap-2">
            <IconAlertTriangle size={16} />
            {error}
          </Alert>
        )}

        {/* Resultado */}
        {analysisResult && (
          <div className="mt-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Resultado da Análise</h6>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => setIsExpanded(true)}
                  className="d-flex align-items-center gap-1"
                  title="Expandir para tela cheia"
                >
                  <IconMaximize size={14} />
                  Expandir
                </Button>
                <Button
                  variant={copied ? "success" : "outline-secondary"}
                  size="sm"
                  onClick={handleCopy}
                  className={`debug-copy-btn ${copied ? "copied" : ""}`}
                >
                  {copied ? "✓ Copiado" : <IconCopy size={14} />}
                </Button>
              </div>
            </div>
            <div
              className="debug-analysis-result p-3"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                fontFamily: "monospace",
                fontSize: "13px",
                lineHeight: "1.6",
                whiteSpace: "pre-wrap",
              }}
            >
              {analysisResult}
            </div>
          </div>
        )}

        {/* Modal de Expansão para Tela Cheia */}
        <Modal
          show={isExpanded}
          onHide={() => setIsExpanded(false)}
          size="xl"
          fullscreen
          className="analysis-expanded-modal"
        >
          <Modal.Header closeButton style={{ borderBottom: "2px solid #e2e8f0" }}>
            <Modal.Title className="d-flex align-items-center gap-2">
              <IconBug size={20} />
              Resultado da Análise - Visualização Expandida
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: "2rem" }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex align-items-center gap-2">
                <IconMessageCircle size={16} className="text-primary" />
                <span className="fw-bold">Agente: {agentContext?.agent_name || 'Não identificado'}</span>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant={copied ? "success" : "outline-secondary"}
                  onClick={handleCopy}
                  className={`d-flex align-items-center gap-1 ${copied ? "copied" : ""}`}
                >
                  <IconCopy size={16} />
                  {copied ? "Copiado!" : "Copiar Texto"}
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setIsExpanded(false)}
                  className="d-flex align-items-center gap-1"
                >
                  <IconMinimize size={16} />
                  Minimizar
                </Button>
              </div>
            </div>
            <div
              className="analysis-expanded-content"
              style={{
                backgroundColor: "#ffffff",
                border: "2px solid #e2e8f0",
                borderRadius: "12px",
                padding: "2rem",
                fontSize: "15px",
                lineHeight: "1.7",
                fontFamily: "system-ui, -apple-system, sans-serif",
                whiteSpace: "pre-wrap",
                height: "calc(100vh - 200px)",
                overflowY: "auto",
                boxShadow: "inset 0 2px 8px rgba(0,0,0,0.05)"
              }}
            >
              {analysisResult}
            </div>
          </Modal.Body>
        </Modal>
      </Modal.Body>
    </Modal>
  );
};

export default ConversationDebugModal;
